import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { ApiQuestDetail } from "griptonite-api-client/types";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useMeProfileQuery } from "@/hooks/profile";
import { useQuestQuery, useUpdateQuestMutation } from "@/hooks/quests";
import { useCurrentVenueUuid } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import BottomButtons, { questStepLink } from "./BottomButtons";
import PublishWarningsAlert from "./PublishWarningsAlert";
import QuestBonuses from "./QuestBonuses";
import QuestBoosts from "./QuestBoosts";
import QuestMilestones from "./QuestMilestones";
import QuestProfileTabs from "./QuestProfileTabs";
import QuestRulesPreview from "./QuestRulesPreview";
import QuestTabLayout from "./QuestTabLayout";
import {
  QuestRulesFormValues,
  makeQuestRulesFormSchema,
  questRulesFormValuesToPayload,
  questToRulesFormValues,
} from "./rulesForms";

// The list entry that stands in while the detail loads has no reward ladder, and a form built
// from it would open empty, so the form waits for the detail.
export default function QuestRules({ questUuid }: { questUuid: string }) {
  const routeVenueUuid = useCurrentVenueUuid();
  const { data: quest } = useQuestQuery(questUuid, routeVenueUuid);

  if (!quest || !("pointsThresholds" in quest)) {
    return null;
  }
  return <QuestRulesForm quest={quest} routeVenueUuid={routeVenueUuid} />;
}

// Milestones, side quests and boosts are edited together and saved in one quest edit.
const QuestRulesForm = ({
  quest,
  routeVenueUuid,
}: {
  quest: ApiQuestDetail;
  routeVenueUuid?: string;
}) => {
  const { data: user } = useMeProfileQuery();
  const updateMutation = useUpdateQuestMutation(quest.uuid, routeVenueUuid);
  const navigate = useNavigate();

  const defaultValues = useMemo(() => questToRulesFormValues(quest), [quest]);
  const schema = useMemo(() => makeQuestRulesFormSchema(quest), [quest]);

  const methods = useForm<QuestRulesFormValues>({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues,
  });

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      current.pathname !== next.pathname && methods.formState.isDirty,
    enableBeforeUnload: methods.formState.isDirty,
    withResolver: true,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmit = async (values: QuestRulesFormValues) => {
    const saved = await updateMutation.mutateAsync(questRulesFormValuesToPayload(values, quest), {
      onError: (error) => handleFormError(error, methods),
    });
    methods.reset(questToRulesFormValues(saved));
    if (saved.isHidden) {
      // The save has succeeded, but the blocker may still see the previous render's dirty state.
      navigate({ ...questStepLink("promotion"), ignoreBlocker: true });
    }
  };

  // The provider spans the preview too, so it can follow the form as it's edited.
  return (
    <FormProvider {...methods}>
      <QuestTabLayout
        // The estimate only models leaderboard points quests.
        aside={
          user?.isStaff && quest.type === "LEADERBOARD_POINTS" ? (
            <QuestRulesPreview quest={quest} />
          ) : undefined
        }
      >
        <QuestProfileTabs questUuid={quest.uuid} />
        <Stack
          sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}
          component="form"
          autoComplete="off"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <Stack sx={{ padding: 1.5, gap: 1, flex: 1, overflowY: "auto", minHeight: 0 }}>
            <PublishWarningsAlert tab="rules" quest={quest} />
            <FormErrorAlert />
            <Stack spacing={3} divider={<Divider />}>
              {quest.type === "LEADERBOARD_POINTS" ? <QuestMilestones quest={quest} /> : null}
              <QuestBonuses />
              <QuestBoosts quest={quest} />
            </Stack>
          </Stack>
          <Box sx={{ padding: 1.5, flexShrink: 0 }}>
            <BottomButtons quest={quest} step="rules" formState={methods.formState} />
          </Box>
        </Stack>
      </QuestTabLayout>
      <Modal open={blocker.status === "blocked"} onClose={blocker.reset}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Discard quest rule changes?</DialogTitle>
          <DialogContent>
            <Typography sx={{ maxWidth: 350 }}>
              You have unsaved changes. Are you sure you want to leave?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button type="button" color="danger" onClick={blocker.proceed} sx={{ flex: 1 }}>
              Discard
            </Button>
            <Button
              type="button"
              color="neutral"
              variant="soft"
              onClick={blocker.reset}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </FormProvider>
  );
};
